import { Button } from "@interstate/components/Button";
import { ArrowTopRightOnSquareIcon } from "@interstate/components/Icons";
import { useToaster } from "@interstate/components/Toast";
import { useContext, useMemo, useState } from "react";
import { Spinner } from "@interstate/components/Spinner";
import {
  ConversationContext,
  ConversationDetailsContext,
  useEmailReply
} from "../../context";
import {
  ConversationDirection,
  ConversationTypeEnum,
  ConversationViewTypeEnum
} from "@coxauto-ui/cxm/communications/interfaces";
import { replyButtonTestId } from "../../testIds";
import {
  SendCommunicationRequest,
  getConversationItemFromSendAction,
  isResponseError,
  sendCommunication
} from "@coxauto-ui/cxm/communications/api";
import { TagManager } from "@coxauto-ui/core-third-party-ga4";
import {
  ConditionalToolTipWrapper,
  normalizeEmptyParagraphs,
  notifyCommunicationSent,
  wrapMediaWidget
} from "@coxauto-ui/cxm/communications/util";
import { replaceStationeryBodyWithContentAndHead } from "../../utils/replace-text-utils";
import { ButtonContainer, removeVideoUrls } from "./message-send-button-shared";
import { buildMediaInfoFromContent } from "../../utils/build-media-utils";
import { FileUploadErrorAlert } from "../reply-alerts";

export interface EmailMessageSendButtonProps {
  onMessageSendFailed: () => void;
  onMessageSendFailedSetMsg: (msg: string) => void;
}

export function EmailMessageSendButton(props: EmailMessageSendButtonProps) {
  const [sendTextResponseMsg, setTextResponseMsg] = useState("");
  const { toast: toaster } = useToaster();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const {
    autoLeadId,
    repairOrderId,
    customerId,
    customerSystem,
    defaultTab,
    env,
    getUserName,
    hasActiveTask,
    setDefaultMessageContent,
    taskId,
    serviceAppointmentId,
    completeTask,
    inboxMessageHandler,
    isIframeMode
  } = useContext(ConversationContext);

  const {
    setConversationItems,
    selectedConversationItem,
    selectedReplyOption,
    toggleSetShowReply
  } = useContext(ConversationDetailsContext);

  const {
    replyText,
    setReplyText,
    replyTextSubject,
    setReplyTextSubject,
    attachments: messageAttachments,
    setAttachments,
    selectedToChannelValue,
    setSelectedToChannelValue,
    selectedStationeryHTML,
    setSelectedStationeryHTML,
    templateHead,
    setTemplateHead,
    ccRecipients,
    setCcRecipients,
    bccRecipients,
    setBccRecipients,
    setSelectedMedia,
    endlinkMediaMap,
    setEndlinkMediaMap
  } = useEmailReply();

  const [loading, setLoading] = useState(false);

  const closeOnSend = () => toggleSetShowReply && toggleSetShowReply();

  const clearEmailReplyFieldsAndDefaults = () => {
    // Clear email reply fields directly
    setReplyText("");
    setReplyTextSubject("");
    setAttachments([]);
    setSelectedToChannelValue("");
    setSelectedStationeryHTML(null);
    setTemplateHead(null);
    setCcRecipients([]);
    setBccRecipients([]);
    setSelectedMedia([]);
    setEndlinkMediaMap({});

    setDefaultMessageContent(null);
  };

  const applyDisabled = useMemo(() => {
    const hasSubject =
      replyTextSubject !== undefined && replyTextSubject?.trim().length > 0;
    return loading || !(replyText?.trim().length > 0 && hasSubject);
  }, [loading, replyText, replyTextSubject]);

  const sendReply = async () => {
    setTextResponseMsg("");

    if (replyText.trim().length === 0 || !selectedReplyOption) {
      return;
    }

    setLoading(true);

    const emailContent = replaceStationeryBodyWithContentAndHead(
      wrapMediaWidget(normalizeEmptyParagraphs(replyText)),
      selectedStationeryHTML || null,
      templateHead || null
    );

    if (selectedReplyOption?.replyInline) {
      const channelValue = selectedToChannelValue || "";
      // Build MediaInfo from what's actually in the editor content,
      // not from the accumulated context — handles deleted widgets.
      const mediaInfo = buildMediaInfoFromContent(
        replyText,
        endlinkMediaMap,
        true
      );

      const sendCommsRequest: SendCommunicationRequest = {
        autoLeadId,
        repairOrderId,
        consumerId: customerId,
        consumerContactInfo: channelValue,
        consumerRecordSystem: customerSystem,
        taskId: hasActiveTask ? taskId : undefined,
        content: emailContent,
        type: ConversationTypeEnum.EMAIL,
        subject: replyTextSubject || "",
        attachments: messageAttachments
          .filter((p: { contentType: string }) => {
            // Remove video attachments.
            return !p.contentType.includes("video/");
          })
          .map((p: { key: string }) => {
            return p.key;
          }),
        ccAddresses: ccRecipients.filter(Boolean),
        bccAddresses: bccRecipients.filter(Boolean),
        serviceAppointmentId,
        mediaInfo
      };

      const response = await sendCommunication(sendCommsRequest, env);
      if (response === true) {
        TagManager.event({
          event: "productEvent",
          eventName: "Message Sent",
          eventProperties: {
            packagedBusinessCapability: "OCC Inbox",
            commonCustomerId: customerId,
            communicationChannel: ConversationTypeEnum.EMAIL,
            replyContext:
              selectedConversationItem != null
                ? ConversationViewTypeEnum.DETAIL
                : ConversationViewTypeEnum.LIST,
            errorMessage: undefined
          }
        });

        // Notify parent window if in iframe mode
        if (isIframeMode && inboxMessageHandler) {
          notifyCommunicationSent(
            inboxMessageHandler,
            "EMAIL",
            ConversationTypeEnum.EMAIL,
            true,
            hasActiveTask,
            replyTextSubject,
            messageAttachments.length > 0
              ? messageAttachments.length
              : undefined
          );
        }

        const conversationItem = getConversationItemFromSendAction(
          getUserName(),
          ConversationDirection.Outbound,
          ConversationTypeEnum.EMAIL,
          channelValue,
          new Date(),
          removeVideoUrls(replyText.substring(0, 300)),
          replyTextSubject || "",
          undefined,
          messageAttachments,
          ccRecipients.filter(Boolean),
          bccRecipients.filter(Boolean)
        );

        setConversationItems(current => [...current, conversationItem]);
        clearEmailReplyFieldsAndDefaults();

        // Complete the task if this was sent on the defaultTab channel
        if (hasActiveTask && defaultTab === ConversationTypeEnum.EMAIL) {
          completeTask();
        }

        closeOnSend();
      } else if (isResponseError(response)) {
        let error = "Failed to send message.";
        if (response?.statusCode === 400) {
          error = response?.errorResult?.message as string;
          if (
            error.includes(
              "The message can only have a maximum of 10 attachments"
            )
          ) {
            error = "The message can only have a maximum of 10 attachments";
            setTextResponseMsg(error);
          }
        }
        TagManager.event({
          event: "productEvent",
          eventName: "Error Displayed",
          eventProperties: {
            packagedBusinessCapability: "OCC Inbox",
            commonCustomerId: customerId,
            communicationChannel: ConversationTypeEnum.EMAIL,
            replyContext: undefined,
            errorMessage: error
          }
        });
        if (response?.statusCode === 403) {
          props.onMessageSendFailedSetMsg("Compliance Issue");
          props.onMessageSendFailed();
        }

        // Notify parent window if in iframe mode
        if (isIframeMode && inboxMessageHandler) {
          notifyCommunicationSent(
            inboxMessageHandler,
            "EMAIL",
            ConversationTypeEnum.EMAIL,
            false,
            hasActiveTask
          );
        }

        if (!error.includes("attachment")) {
          // Clear email channel fields on error
          clearEmailReplyFieldsAndDefaults();
        }
        toaster("Error", error, noop, undefined);
        setLoading(false);
      }
    } else {
      window.open(selectedReplyOption?.replyUrl);
    }
    setLoading(false);
  };

  return (
    <ButtonContainer>
      <FileUploadErrorAlert message={sendTextResponseMsg} />
      <ConditionalToolTipWrapper condition={false} toolTipContent="">
        <Button
          data-testid={replyButtonTestId}
          disabled={applyDisabled}
          id="conversation-reply-button"
          onClick={() => sendReply()}
        >
          {!selectedReplyOption?.replyInline ? (
            <>
              <ArrowTopRightOnSquareIcon /> Reply In External Application
            </>
          ) : (
            "Send Email"
          )}
          {loading && (
            <Spinner
              id="conversation-reply-button-loading-indicator"
              size={30}
            />
          )}
        </Button>
      </ConditionalToolTipWrapper>
    </ButtonContainer>
  );
}

export default EmailMessageSendButton;
